/**
 * @fileoverview Área de Configurações: menu lateral por grupos e uma página por
 * seção (/settings/:section). Quem vê cada seção e para onde vai /settings
 * ficam em registry.js; os dados (carregados uma vez para a área), em
 * useSettingsArea.js; o conteúdo de cada seção, em sections/.
 */

import React, { useContext, useEffect, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useMediaQuery } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { ChevronLeft } from "lucide-react";

import MainContainer from "../../components/MainContainer";
import { LoadError, LoadingState } from "../../components/Settings/LoadState";
import ConfirmationModal from "../../components/ConfirmationModal";
import { AuthContext } from "../../context/Auth/AuthContext";
import useLeaveGuard from "../../hooks/useLeaveGuard";
import { i18n } from "../../translate/i18n";
import SettingsNav from "./SettingsNav";
import SECTIONS from "./sections";
import useSettingsArea from "./useSettingsArea";
import useSettingsStyles from "./styles";
import { buildContext, findItem, resolveRoute, visibleItems } from "./registry";
import usePermission from "../../hooks/usePermission";

const NAV_COLLAPSED_KEY = "settings:navCollapsed";

const readNavCollapsed = () => {
  try {
    return localStorage.getItem(NAV_COLLAPSED_KEY) === "1";
  } catch (err) {
    return false;
  }
};

const writeNavCollapsed = (collapsed) => {
  try {
    localStorage.setItem(NAV_COLLAPSED_KEY, collapsed ? "1" : "0");
  } catch (err) {
    // Sem localStorage o menu só não lembra a escolha.
  }
};

// Primeiro pai que rola: a coluna da seção no computador, o MainContainer no
// celular. Rola-se ele direto: scrollIntoView rolaria também o <main> do layout
// (overflow hidden) e esconderia os avisos do topo.
const scrollParent = (element) => {
  let node = element?.parentElement;
  while (node) {
    const { overflowY } = window.getComputedStyle(node);
    if (overflowY === "auto" || overflowY === "scroll") return node;
    node = node.parentElement;
  }
  return null;
};

const scrollToTop = (element) => {
  const parent = scrollParent(element);
  if (parent) parent.scrollTop = 0;
};

const prefersReducedMotion = () =>
  typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Realce em andamento por elemento: escolher o mesmo campo de novo reinicia.
const highlightTimers = new WeakMap();

// Campo aberto pela busca (#campo): rola até ele, realça e põe o foco na linha
// (não no controle: o Enter que escolheu o resultado abriria um select ou
// acionaria um botão como "Adicionar"). Do foco na linha, o Tab segue para o
// controle, e o leitor de tela anuncia a linha.
const revealSetting = (element, highlightClass) => {
  const parent = scrollParent(element);
  if (parent) {
    const top = parent.scrollTop + element.getBoundingClientRect().top - parent.getBoundingClientRect().top - 96;
    parent.scrollTo({ top: Math.max(0, top), behavior: prefersReducedMotion() ? "auto" : "smooth" });
  }
  if (!element.hasAttribute("tabindex")) element.setAttribute("tabindex", "-1");
  element.focus({ preventScroll: true });
  window.clearTimeout(highlightTimers.get(element));
  element.classList.remove(highlightClass);
  element.getBoundingClientRect(); // força o navegador a recomeçar a animação
  element.classList.add(highlightClass);
  highlightTimers.set(
    element,
    window.setTimeout(() => element.classList.remove(highlightClass), 1700)
  );
};

// #trecho escrito à mão pode vir com escape inválido ("%E0"): ignora, sem quebrar.
const decodeHash = (hash) => {
  try {
    return decodeURIComponent((hash || "").slice(1));
  } catch (err) {
    return "";
  }
};

const SettingsCustom = () => {
  const classes = useSettingsStyles();
  const theme = useTheme();
  // noSsr: sem isto o 1º render diz "não é celular" e o redirecionamento do
  // computador dispararia no celular.
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"), { noSsr: true });
  const history = useHistory();
  const location = useLocation();
  const { section } = useParams();
  const { user, impersonation } = useContext(AuthContext);
  const [navCollapsed, setNavCollapsed] = useState(readNavCollapsed);
  // A seção aberta tem um formulário com alterações não salvas: sair pergunta antes.
  const [dirty, setDirty] = useState(false);
  const headerRef = useRef(null);
  const hashRef = useRef(location.hash);
  hashRef.current = location.hash;

  // Com a regra nova ligada, cada seção pela permissão dela (registry.js): a
  // área espera o acesso da pessoa chegar, senão decidiria pela regra de hoje
  // (e perderia o endereço de uma seção que a pessoa pode abrir).
  const { can, loaded } = usePermission();
  const userReady = !!user?.id && loaded;
  const ctx = buildContext({ user, impersonation, can });
  // A área busca só o que alguma seção que o perfil vê usa (o atendente, que
  // vê só a própria Segurança, não busca nada daqui).
  const visible = visibleItems(ctx);
  const area = useSettingsArea({
    enabled: userReady,
    needsSettings: visible.some((entry) => SECTIONS[entry.id].settings),
    needsCompany: visible.some((entry) => SECTIONS[entry.id].company),
    companyId: user?.companyId,
  });
  const route = resolveRoute({ section, hash: location.hash, ctx, ready: userReady, isMobile });
  // Seção que deixou de existir para o perfil não tem como guardar a edição: o
  // redirecionamento não pergunta (senão ficaria parado numa página vazia).
  const guard = useLeaveGuard(dirty && !route.redirect, i18n.t("unsavedChanges.leaveConfirm"));
  const item = route.show ? findItem(route.show) : null;

  useEffect(() => {
    // Com #campo (busca), quem rola é a seção, até o campo; voltar ao topo aqui
    // desfaria isso (este efeito roda depois do da seção).
    if (item && !hashRef.current) scrollToTop(headerRef.current);
    // Seção nova começa sem alterações pendentes.
    setDirty(false);
  }, [item]);

  // Redireciona por efeito (e não com <Redirect>): assim o guard acima já foi
  // desligado quando a troca de URL acontece.
  useEffect(() => {
    if (route.redirect) history.replace(route.redirect);
  }, [route.redirect, history]);

  if (route.redirect) return null;

  const setCollapsed = (collapsed) => {
    setNavCollapsed(collapsed);
    writeNavCollapsed(collapsed);
  };

  const backToList = () =>
    guard.leave(() => (location.state?.fromList ? history.goBack() : history.push("/settings")));

  // No computador o menu fica sempre à vista (recolhido, só os ícones).
  const showNav = isMobile ? !!route.list : true;
  const showContent = !(isMobile && route.list);

  return (
    <MainContainer className={classes.container}>
      <div className={classes.layout}>
        {showNav && (
          <aside
            className={[classes.navColumn, !isMobile && navCollapsed ? classes.navColumnCollapsed : ""]
              .filter(Boolean)
              .join(" ")}
          >
            <SettingsNav
              ctx={ctx}
              activeId={item?.id}
              isMobile={isMobile}
              collapsed={!isMobile && navCollapsed}
              onToggleCollapsed={isMobile ? undefined : () => setCollapsed(!navCollapsed)}
            />
          </aside>
        )}

        {showContent && (
          <section className={classes.content}>
            <div className={[classes.inner, item && SECTIONS[item.id].wide ? classes.innerWide : ""].filter(Boolean).join(" ")}>
              {item ? (
                <SectionPage
                  classes={classes}
                  item={item}
                  area={area}
                  ctx={ctx}
                  headerRef={headerRef}
                  isMobile={isMobile}
                  onBack={backToList}
                  onDirtyChange={setDirty}
                />
              ) : (
                <LoadingState />
              )}
            </div>
          </section>
        )}
      </div>

      <ConfirmationModal
        title={i18n.t("unsavedChanges.title")}
        open={guard.confirmOpen}
        onClose={guard.cancel}
        onConfirm={guard.confirm}
        confirmLabel={i18n.t("unsavedChanges.discard")}
      >
        {i18n.t("unsavedChanges.body")}
      </ConfirmationModal>
    </MainContainer>
  );
};

const SectionPage = ({ classes, item, area, ctx, headerRef, isMobile, onBack, onDirtyChange }) => {
  const location = useLocation();
  const section = SECTIONS[item.id];
  const Content = section.component;
  const waitsForArea = !section.standalone;

  // Veio da busca com #campo: espera o campo existir (os dados podem estar
  // chegando) e o mostra. location.key muda a cada clique, mesmo no mesmo campo.
  const fieldId = decodeHash(location.hash);
  useEffect(() => {
    if (!fieldId) return undefined;
    let frame;
    let tries = 0;
    const find = () => {
      const element = document.querySelector(`[data-setting-id="${window.CSS.escape(fieldId)}"]`);
      if (element) revealSetting(element, classes.settingHighlight);
      else if (tries++ < 120) frame = requestAnimationFrame(find);
    };
    find();
    return () => cancelAnimationFrame(frame);
  }, [fieldId, location.key, item.id, area.status, classes.settingHighlight]);

  let body;
  if (waitsForArea && area.status === "loading") {
    body = <LoadingState />;
  } else if (waitsForArea && area.status === "error") {
    body = <LoadError onRetry={area.reload} />;
  } else {
    body = <Content area={area} ctx={ctx} onDirtyChange={onDirtyChange} />;
  }

  return (
    <>
      <header className={classes.header} ref={headerRef}>
        <div className={classes.headerTop}>
          {isMobile ? (
            <button type="button" className={classes.backButton} onClick={onBack}>
              <ChevronLeft size={18} strokeWidth={1.8} aria-hidden="true" />
              {i18n.t("settings.title")}
            </button>
          ) : (
            <span className={classes.eyebrow}>{i18n.t(`settings.groups.${item.group}`)}</span>
          )}
        </div>
        <h1 className={classes.title}>{i18n.t(`settings.sections.${item.id}.title`)}</h1>
        <p className={classes.description}>{i18n.t(`settings.sections.${item.id}.description`)}</p>
        {section.autosave && <p className={classes.autosave}>{i18n.t("settings.area.autosave")}</p>}
      </header>
      <div className={classes.body}>{body}</div>
    </>
  );
};

export default SettingsCustom;
